/**
 * Picture-in-picture has two browser surfaces: the standard API used by
 * Chromium and the WebKit presentation-mode API used by Safari. Keeping the
 * capability checks here prevents the player lifecycle from accidentally
 * treating Safari as unsupported (or from leaving native controls behind).
 */
type WebKitPictureInPictureVideo = HTMLVideoElement & {
	webkitPresentationMode?: 'inline' | 'fullscreen' | 'picture-in-picture';
	webkitSetPresentationMode?: (mode: 'inline' | 'fullscreen' | 'picture-in-picture') => void;
	webkitSupportsPresentationMode?: (mode: 'inline' | 'fullscreen' | 'picture-in-picture') => boolean;
};

export type VideoPictureInPictureCapability = 'available' | 'installed_app_unsupported' | 'browser_unsupported';

const pipExitHandoffCancelers = new WeakMap<HTMLVideoElement, () => void>();
const pipObserverIntentSetters = new WeakMap<HTMLVideoElement, (playing: boolean) => void>();
const webKitPictureInPictureReturningSessions = new WeakMap<HTMLVideoElement, object>();

/** Cancels a pending PiP-to-inline replay without assigning a user intent. */
export function cancelVideoPictureInPictureExitHandoff(video: HTMLVideoElement): void {
	pipExitHandoffCancelers.get(video)?.();
}

/** Records an app or Media Session playback intent while PiP is active. */
export function setVideoPictureInPictureExplicitPlaybackIntent(video: HTMLVideoElement, playing: boolean): void {
	pipObserverIntentSetters.get(video)?.(playing);
}

export function hasWebKitPictureInPictureEvidence(video: HTMLVideoElement): boolean {
	const webkit = webkitVideo(video);
	return typeof webkit.webkitSupportsPresentationMode === 'function' || typeof webkit.webkitSetPresentationMode === 'function';
}

/**
 * WebKit can emit one more inline teardown pause after leave-PiP. Keep a
 * bounded listener for that actual event; an explicit user pause cancels it.
 */
export function beginVideoPictureInPictureExitPlaybackHandoff(video: HTMLVideoElement, resume: () => void): () => void {
	cancelVideoPictureInPictureExitHandoff(video);
	let active = true;
	let timeout: number | undefined;
	const resumeIfPlayable = () => {
		if (active && !video.ended) resume();
	};
	const onPause = () => resumeIfPlayable();
	const onEnded = () => cancel();
	const cancel = () => {
		if (!active) return;
		active = false;
		if (timeout !== undefined) window.clearTimeout(timeout);
		video.removeEventListener('pause', onPause);
		video.removeEventListener('ended', onEnded);
		if (pipExitHandoffCancelers.get(video) === cancel) pipExitHandoffCancelers.delete(video);
	};
	video.addEventListener('pause', onPause);
	video.addEventListener('ended', onEnded);
	resumeIfPlayable();
	timeout = window.setTimeout(cancel, 1_500);
	pipExitHandoffCancelers.set(video, cancel);
	return cancel;
}

function webkitVideo(video: HTMLVideoElement): WebKitPictureInPictureVideo {
	return video as WebKitPictureInPictureVideo;
}

export function isStandaloneWebApp(): boolean {
	if (typeof window === 'undefined') return false;
	const navigatorWithStandalone = navigator as Navigator & { standalone?: boolean };
	return navigatorWithStandalone.standalone === true
		|| window.matchMedia?.('(display-mode: standalone)').matches === true;
}

export function videoPictureInPictureCapability(video: HTMLVideoElement): VideoPictureInPictureCapability {
	const webkit = webkitVideo(video);
	// Installed iOS/iPadOS web apps can expose pictureInPictureEnabled and
	// requestPictureInPicture while the WebKit video presentation capability is
	// actually disabled. When this authoritative WebKit probe exists, it must
	// win over the misleading standard flags. Chromium/Android do not expose it
	// and continue through the standard API path below.
	if (typeof webkit.webkitSupportsPresentationMode === 'function') {
		if (webkit.webkitSupportsPresentationMode('picture-in-picture') && typeof webkit.webkitSetPresentationMode === 'function') return 'available';
		return isStandaloneWebApp() ? 'installed_app_unsupported' : 'browser_unsupported';
	}
	const standard = typeof document !== 'undefined'
		&& document.pictureInPictureEnabled === true
		&& typeof video.requestPictureInPicture === 'function';
	return standard || typeof webkit.webkitSetPresentationMode === 'function' ? 'available' : 'browser_unsupported';
}

export function supportsVideoPictureInPicture(video: HTMLVideoElement): boolean {
	return videoPictureInPictureCapability(video) === 'available';
}

export function isVideoPictureInPictureActive(video: HTMLVideoElement): boolean {
	return (typeof document !== 'undefined' && document.pictureInPictureElement === video)
		|| webkitVideo(video).webkitPresentationMode === 'picture-in-picture';
}

/** Includes WebKit's PiP → native-fullscreen return gap until it reaches inline. */
export function isVideoPictureInPictureSessionActive(video: HTMLVideoElement): boolean {
	return isVideoPictureInPictureActive(video) || webKitPictureInPictureReturningSessions.has(video);
}

/**
 * Only call this from a user gesture or a browser-originated Media Session
 * action. Browsers intentionally reject page-hidden script initiated PiP.
 */
export async function toggleVideoPictureInPicturePresentation(video: HTMLVideoElement): Promise<boolean> {
	if (!supportsVideoPictureInPicture(video)) return false;
	if (isVideoPictureInPictureActive(video)) {
		if (document.pictureInPictureElement === video && typeof document.exitPictureInPicture === 'function') {
			await document.exitPictureInPicture();
		} else {
			webkitVideo(video).webkitSetPresentationMode?.('inline');
		}
		return false;
	}
	// Keep the current playback state. Standard PiP supports paused media, and
	// forcing play here turns a user's paused PiP session into a false resume
	// when it returns to the page.
	// WebKit presentation owns the whole session when its evidence exists.
	// Do not mix it with the standard branch during a return transition.
	if (hasWebKitPictureInPictureEvidence(video)) {
		// WebKit flips webkitPresentationMode asynchronously (the animated PiP
		// entry can land long after this call), so probing it synchronously
		// reads 'inline' and made callers treat a started session as failed —
		// which let a navigation tear the live window down. The capability
		// probe above already confirmed the mode is supported, so the issued
		// request is the truth for the enter path.
		webkitVideo(video).webkitSetPresentationMode?.('picture-in-picture');
		return true;
	} else if (typeof video.requestPictureInPicture === 'function' && document.pictureInPictureEnabled === true) {
		await video.requestPictureInPicture();
	}
	return isVideoPictureInPictureActive(video);
}

/**
 * Closes an active system PiP surface without changing playback intent. This
 * does not rely on the capability probe because a session can still need
 * teardown while the browser changes presentation capabilities.
 */
export async function exitVideoPictureInPicturePresentation(video: HTMLVideoElement): Promise<void> {
	if (document.pictureInPictureElement === video && typeof document.exitPictureInPicture === 'function') {
		await document.exitPictureInPicture();
		return;
	}
	if (webkitVideo(video).webkitPresentationMode === 'picture-in-picture') {
		webkitVideo(video).webkitSetPresentationMode?.('inline');
	}
}

/**
 * Registers both standards and guarantees a single cleanup-safe exit.
 *
 * iOS can pause the inline element while returning from PiP before it emits
 * its presentation-mode exit. Keep the last confirmed PiP intent and hand it
 * to the page restoration callback rather than sampling `video.paused` after
 * that teardown pause.
 */
export function observeVideoPictureInPictureExit(
	video: HTMLVideoElement,
	onExit: (wasPlaying: boolean) => void,
	onWebKitReturning?: () => void,
): () => void {
	let settled = false;
	let webKitReturning = false;
	const session = {};
	let inPictureInPicture = isVideoPictureInPictureActive(video);
	let desiredPlayback = !video.paused;
	const stop = () => {
		if (webKitPictureInPictureReturningSessions.get(video) === session) webKitPictureInPictureReturningSessions.delete(video);
		video.removeEventListener('leavepictureinpicture', handle);
		video.removeEventListener('webkitpresentationmodechanged', handle);
		video.removeEventListener('enterpictureinpicture', handle);
		video.removeEventListener('play', onPlay);
		video.removeEventListener('pause', onPause);
		video.removeEventListener('ended', onEnded);
		if (pipObserverIntentSetters.get(video) === setExplicitIntent) pipObserverIntentSetters.delete(video);
	};
	const usesWebKitPresentation = hasWebKitPictureInPictureEvidence(video);
	const enter = () => {
		if (inPictureInPicture || !isVideoPictureInPictureActive(video)) return;
		inPictureInPicture = true;
		desiredPlayback = !video.paused;
	};
	const handle = () => {
		if (settled) return;
		if (usesWebKitPresentation) {
			const mode = webkitVideo(video).webkitPresentationMode;
			if (mode === 'picture-in-picture') {
				if (webKitPictureInPictureReturningSessions.get(video) === session) webKitPictureInPictureReturningSessions.delete(video);
				enter();
				return;
			}
			// Safari can return through native fullscreen. That is still system
			// presentation, not an inline page restoration.
			if (mode === 'fullscreen') {
				if (!webKitReturning) {
					webKitReturning = true;
					webKitPictureInPictureReturningSessions.set(video, session);
					onWebKitReturning?.();
				}
				return;
			}
		} else if (isVideoPictureInPictureActive(video)) {
			enter();
			return;
		}
		if (!inPictureInPicture) return;
		settled = true;
		stop();
		onExit(desiredPlayback);
	};
	const onPlay = () => {
		if (inPictureInPicture && isVideoPictureInPictureActive(video)) desiredPlayback = true;
	};
	const onPause = () => {
		if (inPictureInPicture && isVideoPictureInPictureActive(video) && !usesWebKitPresentation) desiredPlayback = false;
	};
	const onEnded = () => {
		if (inPictureInPicture) desiredPlayback = false;
	};
	const setExplicitIntent = (playing: boolean) => {
		// A page or Media Session command is authoritative even during WebKit's
		// inline-before-exit liminal window, where the active probe is already
		// false but the observer still owns the pending PiP session.
		if (inPictureInPicture && !settled) desiredPlayback = playing;
	};
	video.addEventListener('leavepictureinpicture', handle);
	video.addEventListener('webkitpresentationmodechanged', handle);
	video.addEventListener('enterpictureinpicture', handle);
	video.addEventListener('play', onPlay);
	video.addEventListener('pause', onPause);
	video.addEventListener('ended', onEnded);
	pipObserverIntentSetters.set(video, setExplicitIntent);
	return () => {
		settled = true;
		stop();
	};
}
